import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SmsConfirmDialog } from "@/components/SmsConfirmDialog";
import { EventTimeline, ContactDialog, NoteBox, LockoutButtons, confirmIfRecent, describeContact, fmtWhen } from "@/components/TenantTimeline";
import { AlertTriangle, RefreshCw, Copy, MessageSquareText, PhoneCall, Lock, ChevronDown, ChevronRight, Loader2, CircleDollarSign, LockOpen } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const nzDate = (ymd) => new Date(ymd + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });

const Badge = ({ cls, children, testid }) => <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{children}</span>;

const InvoiceTable = ({ invoices }) => (
  <table className="w-full text-xs" data-testid="overdue-invoice-table">
    <thead><tr className="text-left text-slate-500"><th className="py-1">Invoice</th><th>Due</th><th className="text-right">Original</th><th className="text-right">Owing</th></tr></thead>
    <tbody>{invoices.map((i) => (
      <tr key={i.invoice_id} className="border-t border-slate-100"><td className="py-1 font-mono-nums">{i.invoice_number}{i.reference ? <span className="text-slate-400"> · {i.reference}</span> : null}</td><td>{nzDate(i.due_date)} <span className="text-red-600">({i.days_overdue}d)</span></td><td className="text-right font-mono-nums">{money(i.total)}</td><td className="text-right font-mono-nums font-semibold">{money(i.amount_due)}</td></tr>
    ))}</tbody>
  </table>
);

const CopyDialog = ({ row, onClose, onContact }) => {
  const [text, setText] = useState(row?.copy_message || "");
  const [copied, setCopied] = useState(false);
  useEffect(() => { setText(row?.copy_message || ""); setCopied(false); }, [row]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Message copied"); } catch { toast.error("Could not copy. Select the text and copy it manually."); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="copy-message-dialog">
        <DialogHeader><DialogTitle>Copy message</DialogTitle><DialogDescription>Edit if needed, copy, then paste into WhatsApp, Messenger or email.</DialogDescription></DialogHeader>
        <Textarea data-testid="copy-message-text" rows={6} value={text} onChange={(e) => setText(e.target.value)} />
        {copied && row?.unit_id && <p className="text-sm text-slate-700" data-testid="copy-mark-prompt">Mark as contacted?</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {copied && row?.unit_id ? (
            <Button data-testid="copy-mark-contacted" onClick={() => { onClose(); onContact(row); }} className="bg-blue-600 hover:bg-blue-700">Mark as contacted</Button>
          ) : (
            <Button data-testid="copy-message-btn" onClick={copy} className="gap-1"><Copy className="h-4 w-4" /> Copy</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RowActions = ({ row, onCopy, onContact, onRemind, reload }) => (
  <div className="flex flex-wrap gap-2">
    {row.unit_id && <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => onRemind(row)} data-testid={`overdue-remind-${row.id}`}><MessageSquareText className="h-4 w-4" /> Send Payment Reminder</Button>}
    <Button size="sm" variant="outline" className="gap-1" onClick={() => onCopy(row)} data-testid={`overdue-copy-${row.id}`}><Copy className="h-4 w-4" /> Copy message</Button>
    {row.unit_id && <Button size="sm" variant="outline" className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => onContact(row)} data-testid={`overdue-contacted-${row.id}`}><PhoneCall className="h-4 w-4" /> Mark as contacted</Button>}
    {row.unit_id && <LockoutButtons unit={{ id: row.unit_id, unit_number: row.unit_number, status: "occupied", gate_locked: row.gate_locked }} balance={row.balance} onDone={reload} />}
  </div>
);

const TenantRow = ({ row, open, onToggle, ...actions }) => (
  <Fragment>
    <tr onClick={onToggle} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer align-top" data-testid={`overdue-row-${row.id}`}>
      <td className="px-3 py-3">{open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</td>
      <td className="px-2 py-3"><p className="font-mono-nums font-bold text-slate-900">{row.unit_number || "—"}</p><p className="text-xs text-slate-500">{row.site_name}</p></td>
      <td className="px-2 py-3">
        <p className="font-medium text-slate-900">{row.tenant_name}</p>
        <p className="text-xs text-slate-500">{row.phone || "No phone"}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {row.not_yet_contacted && <Badge cls="bg-amber-50 text-amber-700 border-amber-300" testid={`badge-not-contacted-${row.id}`}>Not yet contacted</Badge>}
          {row.recently_contacted && <Badge cls="bg-blue-50 text-blue-700 border-blue-300" testid={`badge-recent-${row.id}`}>Recently contacted</Badge>}
          {row.gate_locked && <Badge cls="bg-red-50 text-red-700 border-red-300" testid={`badge-locked-${row.id}`}><Lock className="h-3 w-3" /> Locked out</Badge>}
          {row.check_match && <Badge cls="bg-orange-50 text-orange-700 border-orange-300">Check match</Badge>}
        </div>
      </td>
      <td className="px-2 py-3 text-right"><p className="font-mono-nums font-bold text-red-600" data-testid={`overdue-balance-${row.id}`}>{money(row.balance)}</p><p className="text-xs text-slate-500">{row.invoices.length} unpaid</p></td>
      <td className="px-2 py-3"><p className="text-sm">{nzDate(row.oldest_due)}</p><p className="text-xs text-red-600 font-medium">{row.days_overdue} days overdue</p></td>
      <td className="px-2 py-3 text-xs text-slate-600 max-w-[260px]">
        <p>{row.match === "shed" ? "Matched by shed number" : row.match === "name" ? "Matched by name" : "Unmatched"}</p>
        {row.last_contact && <p className="text-slate-500 mt-0.5">{describeContact(row.last_contact)}</p>}
        {row.latest_note && <p className="mt-0.5 italic text-slate-500 truncate" title={row.latest_note.text}>Note: {row.latest_note.text}</p>}
      </td>
    </tr>
    {open && (
      <tr className="bg-slate-50/60 border-b border-slate-200" data-testid={`overdue-detail-${row.id}`}>
        <td colSpan={6} className="px-4 py-4">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-3"><InvoiceTable invoices={row.invoices} /><RowActions row={row} {...actions} /></div>
            {row.unit_id ? (
              <div className="space-y-3"><NoteBox unitId={row.unit_id} onDone={actions.reload} /><EventTimeline events={row.events} onChanged={actions.reload} /></div>
            ) : <p className="text-xs text-slate-500">This invoice couldn't be matched to a shed. Check the Reference or contact name in Xero.</p>}
          </div>
        </td>
      </tr>
    )}
  </Fragment>
);

const StatusBar = ({ data, checking, onCheck }) => (
  <div className="flex flex-wrap items-center gap-3 text-sm">
    <span className="text-slate-500" data-testid="overdue-last-checked">Last checked: {data.last_success ? fmtWhen(data.last_success) : "never"}</span>
    <Button size="sm" variant="outline" onClick={onCheck} disabled={checking || !data.connected} className="gap-1" data-testid="overdue-check-now"><RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} /> Check now</Button>
  </div>
);

export default function Overdue() {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [site, setSite] = useState("ALL");
  const [sort, setSort] = useState("default");
  const [copyRow, setCopyRow] = useState(null);
  const [contactRow, setContactRow] = useState(null);
  const [remind, setRemind] = useState(null);
  const [remindBusy, setRemindBusy] = useState(false);

  const load = useCallback(() => api.getOverdue().then(setData).catch(() => toast.error("Could not load overdue list")), []);
  useEffect(() => { load(); }, [load]);

  const check = async () => {
    setChecking(true);
    try { await api.overdueCheckNow(); toast.success("Checked Xero"); } catch (e) { toast.error(e.response?.data?.detail || "Xero check failed"); }
    finally { setChecking(false); load(); }
  };
  const onCopy = (row) => { if (confirmIfRecent(row)) setCopyRow(row); };
  const onContact = (row) => { if (confirmIfRecent(row)) setContactRow(row); };
  const onRemind = async (row) => {
    if (!confirmIfRecent(row)) return;
    try { setRemind({ row, preview: await api.paymentReminder(row.unit_id, false) }); } catch { toast.error("Could not prepare reminder"); }
  };
  const sendRemind = async () => {
    setRemindBusy(true);
    try {
      const r = await api.paymentReminder(remind.row.unit_id, true);
      if (r.sent) toast.success("Payment reminder sent and logged"); else toast.error(r.send_error || "Reminder not sent");
      setRemind(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setRemindBusy(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const l = data.tenants.filter((r) => site === "ALL" || r.site === site);
    if (sort === "balance") return [...l].sort((a, b) => b.balance - a.balance);
    if (sort === "days") return [...l].sort((a, b) => b.days_overdue - a.days_overdue);
    return l;
  }, [data, site, sort]);

  const actions = { onCopy, onContact, onRemind, reload: load };
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4"><Brand subtitle="Overdue rent" /><ViewNav /></div>
      </header>
      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-5" data-testid="overdue-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><CircleDollarSign className="h-6 w-6 text-red-600" /> Overdue Rent</h2>
            <p className="text-sm text-slate-500 mt-1">Unpaid Xero invoices more than {data?.settings?.overdue_days ?? 3} days past due. Reconcile in Xero as usual — balances update on the next check.</p>
          </div>
          {data && <StatusBar data={data} checking={checking} onCheck={check} />}
        </div>

        {!data ? <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div> : (
          <>
            {!data.connected && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="overdue-not-connected">Xero isn't connected yet. An admin can connect it in Admin → Xero.</div>}
            {data.needs_reconnect && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="overdue-reconnect">Xero needs reconnecting. An admin can do this in Admin → Xero.</div>}
            {data.failing && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="overdue-failing">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>Xero check failing — the list below is from the last good check.{data.last_error ? ` Reason: ${data.last_error}` : ""}</span>
              </div>
            )}
            {data.restore_access.length > 0 && (
              <div className="rounded-xl border border-green-300 bg-green-50 p-4 space-y-2" data-testid="restore-access-list">
                <p className="font-semibold text-green-800 flex items-center gap-2"><LockOpen className="h-4 w-4" /> Paid — restore gate access</p>
                {data.restore_access.map((u) => (
                  <div key={u.unit_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`restore-row-${u.unit_id}`}>
                    <span><b className="font-mono-nums">{u.unit_number}</b> · {u.tenant_name} <span className="text-slate-500">— locked {fmtWhen(u.gate_locked_at)}{u.gate_locked_reason ? ` (${u.gate_locked_reason})` : ""}</span></span>
                    <LockoutButtons unit={{ id: u.unit_id, unit_number: u.unit_number, gate_locked: true }} onDone={load} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Select value={site} onValueChange={setSite}><SelectTrigger className="w-44 bg-white" data-testid="overdue-site-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All sites</SelectItem><SelectItem value="OTOROHANGA">Otorohanga</SelectItem><SelectItem value="RAZZA">Razza</SelectItem><SelectItem value="TE KUITI">Te Kuiti</SelectItem></SelectContent></Select>
              <Select value={sort} onValueChange={setSort}><SelectTrigger className="w-56 bg-white" data-testid="overdue-sort"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="default">Not yet contacted first</SelectItem><SelectItem value="balance">Highest balance</SelectItem><SelectItem value="days">Most days overdue</SelectItem></SelectContent></Select>
              <span className="text-sm text-slate-500" data-testid="overdue-count">{rows.length} tenant{rows.length === 1 ? "" : "s"} overdue{data.unmatched.length ? ` + ${data.unmatched.length} unmatched` : ""}</span>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]" data-testid="overdue-table">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50"><th className="w-8" /><th className="px-2 py-2.5">Shed</th><th className="px-2">Tenant</th><th className="px-2 text-right">Overdue</th><th className="px-2">Oldest due</th><th className="px-2">Match / last contact</th></tr></thead>
                <tbody>
                  {rows.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-slate-400 italic" data-testid="overdue-empty">No overdue tenants.</td></tr>
                    : rows.map((r) => <TenantRow key={r.id} row={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} {...actions} />)}
                </tbody>
              </table>
            </div>
            {data.unmatched.length > 0 && (
              <div className="bg-white border border-orange-200 rounded-xl shadow-sm overflow-x-auto" data-testid="overdue-unmatched">
                <p className="px-4 py-3 font-semibold text-orange-800 border-b border-orange-100">Unmatched ({data.unmatched.length}) — no shed number or tenant name match</p>
                <table className="w-full text-sm min-w-[860px]"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50"><th className="w-8" /><th className="px-2 py-2.5">Shed</th><th className="px-2">Xero contact</th><th className="px-2 text-right">Overdue</th><th className="px-2">Oldest due</th><th className="px-2">Match</th></tr></thead><tbody>
                  {data.unmatched.map((r) => <TenantRow key={r.id} row={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} {...actions} />)}
                </tbody></table>
              </div>
            )}
          </>
        )}
      </main>
      <CopyDialog row={copyRow} onClose={() => setCopyRow(null)} onContact={(r) => setContactRow(r)} />
      <ContactDialog open={!!contactRow} onOpenChange={(o) => !o && setContactRow(null)} unitId={contactRow?.unit_id} title={contactRow ? `${contactRow.unit_number} · ${contactRow.tenant_name} · ${money(contactRow.balance)} overdue` : ""} onDone={load} />
      <SmsConfirmDialog open={!!remind} onOpenChange={(o) => !o && setRemind(null)} preview={remind?.preview} busy={remindBusy} onConfirm={sendRemind} confirmLabel="Send Reminder" title="Send Payment Reminder" description="A payment reminder text will be sent to this tenant and logged as a contact." />
    </div>
  );
}
